
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { tokenService } from '@/services/tokenService';
import { useAuth } from '@/contexts/AuthContext';
import { ResultsLayout } from '@/components/results/ResultsLayout';
import { PromptGeneration } from '@/components/results/PromptGeneration';
import { useToast } from '@/hooks/use-toast';

const PromptAssist = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { toast } = useToast();
  
  // State for prompt management
  const [projectDescription, setProjectDescription] = useState('');
  const [additionalDescription, setAdditionalDescription] = useState('');
  const [generatedPrompts, setGeneratedPrompts] = useState<string[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [chatMessages, setChatMessages] = useState<{type: 'user' | 'ai'; content: string}[]>([]);
  const [showTypingEffect, setShowTypingEffect] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Check if user is authenticated and has tokens
  useEffect(() => {
    const checkAuthAndTokens = async () => {
      if (!currentUser) {
        navigate('/?showAuth=true');
        return;
      }
      
      // Load previous project description if available
      const savedDescription = sessionStorage.getItem('projectDescription');
      if (savedDescription) {
        setProjectDescription(savedDescription);
        
        // Generate initial prompts if none exist yet
        if (generatedPrompts.length === 0) {
          handleRegenerate(
            savedDescription, 
            '', 
            (value: boolean) => setIsGenerating(value),
            (value: boolean) => setShowTypingEffect(value),
            setChatMessages,
            setGeneratedPrompts
          );
        }
      }
    };
    
    checkAuthAndTokens();
  }, [currentUser, navigate, generatedPrompts.length]);

  // Main function to generate prompts
  const handleRegenerate = async (
    description: string, 
    additionalDetails: string, 
    setGenStatus: (value: boolean) => void, 
    setTypeEffect: (value: boolean) => void, 
    setChatMsgs: React.Dispatch<React.SetStateAction<{type: 'user' | 'ai'; content: string}[]>>,
    setPrompts: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    if (!currentUser || !description.trim()) return;
    
    // Check if user has available tokens
    const success = await tokenService.useTokens(currentUser.uid, 1);
    if (!success) {
      toast({
        title: "Insufficient Credits",
        description: "Please purchase more credits to continue.",
        variant: "destructive"
      });
      navigate('/pricing');
      return;
    }

    // Store project description in session storage
    sessionStorage.setItem('projectDescription', description);
    
    // Generate prompts using the PromptGeneration service
    await PromptGeneration.handleRegeneratePrompts(
      description,
      additionalDetails,
      setGenStatus,
      setTypeEffect,
      setChatMsgs,
      setPrompts
    );
  };

  // Function to regenerate a specific prompt
  const handleRegenerateSpecificPrompt = async (index: number) => {
    if (!currentUser) {
      navigate('/?showAuth=true');
      return;
    }

    // Check if user has available tokens
    const success = await tokenService.useTokens(currentUser.uid, 1);
    if (!success) {
      toast({
        title: "Insufficient Credits",
        description: "Please purchase more credits to continue.",
        variant: "destructive"
      });
      navigate('/pricing');
      return;
    }

    await PromptGeneration.regenerateSpecificPrompt(
      index,
      projectDescription,
      additionalDescription,
      generatedPrompts,
      setGeneratedPrompts,
      setChatMessages,
      setIsGenerating
    );
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <ResultsLayout
      children={null} /* Added the missing children prop */
      projectDescription={projectDescription}
      setProjectDescription={setProjectDescription}
      additionalDescription={additionalDescription}
      setAdditionalDescription={setAdditionalDescription}
      generatedPrompts={generatedPrompts}
      isGenerating={isGenerating}
      chatMessages={chatMessages}
      setChatMessages={setChatMessages}
      showTypingEffect={showTypingEffect}
      isFullscreen={isFullscreen}
      toggleFullscreen={toggleFullscreen}
      onRegeneratePrompt={handleRegenerateSpecificPrompt}
      onRegenerate={handleRegenerate}
      currentUser={currentUser}
      setIsGenerating={setIsGenerating}
      setShowTypingEffect={setShowTypingEffect}
      setGeneratedPrompts={setGeneratedPrompts}
    />
  );
};

export default PromptAssist;
